// src/components/hechos/MapaMexico.jsx
//
// Mapa de calor de la República con DOS termómetros por estado:
//   · mitad izquierda, AZUL  → grupo Vale (Vale Amigo, Viva Vale, Rapivale)
//   · mitad derecha,  ROJA   → Presico y sus marcas (La Casita, La Güerita…)
// La intensidad de cada mitad sube con los casos de su grupo, sobre una escala
// compartida (el azul y el rojo son comparables entre sí). Los números exactos
// van en el tooltip y en la tabla — el color nunca carga el dato solo.
//
// La partición se hace con clipPaths sobre la caja delimitadora de cada
// estado, medida con getBBox() al montar.
//
// Clic en un estado con casos → onSelect(estado) para enfocar el tablero;
// clic en el ya enfocado → onSelect('').
//
// Trazos: @svg-maps/mexico (MIT). Cruce de nombres normalizado con alias
// ("Mexico City" vs CDMX, "México" vs Estado de México).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import mexico from '@svg-maps/mexico';
import { fmtMonto } from './ui';

const norm = (s) => String(s || '').toLowerCase()
    .normalize('NFD').replace(/\p{M}/gu, '').trim();

// nombre del módulo (catálogo de zonas) → nombre del paquete
const ALIAS = {
    'cdmx': 'mexico city',
    'ciudad de mexico': 'mexico city',
    'distrito federal': 'mexico city',
    'estado de mexico': 'mexico',
    'edomex': 'mexico',
    'coahuila de zaragoza': 'coahuila',
    'michoacan de ocampo': 'michoacan',
    'veracruz de ignacio de la llave': 'veracruz',
};
const clave = (estado) => ALIAS[norm(estado)] || norm(estado);

const tono = (casos, max, color) => casos
    ? `color-mix(in srgb, var(--ho-${color}) ${14 + Math.round((casos / max) * 76)}%, var(--c-card))`
    : 'var(--c-msg-bg)';

export default function MapaMexico({ data, seleccionado, onSelect }) {
    const [hover, setHover] = useState(null);
    const [cajas, setCajas] = useState({});   // id del estado → bbox
    const svgRef = useRef(null);

    // clave del paquete → { estado, casos, monto, casosVale, casosPresico }
    const valores = useMemo(() => {
        const out = {};
        for (const e of (data || [])) {
            if (e?.estado) out[clave(e.estado)] = e;
        }
        return out;
    }, [data]);

    // Escala compartida entre ambos grupos, para que las mitades se comparen.
    const max = Math.max(1, ...Object.values(valores)
        .flatMap(v => [v.casosVale || 0, v.casosPresico || 0]));
    const claveSel = seleccionado ? clave(seleccionado) : null;

    // Caja de cada estado, para partirlo a la mitad con clipPaths.
    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;
        const out = {};
        svg.querySelectorAll('path[data-id]').forEach(p => {
            const b = p.getBBox();
            out[p.dataset.id] = { x: b.x, y: b.y, w: b.width, h: b.height };
        });
        setCajas(out);
    }, []);

    function moverTooltip(e, loc, v) {
        const r = e.currentTarget.ownerSVGElement.getBoundingClientRect();
        setHover({
            nombre: v?.estado || loc.name,
            casos: v?.casos || 0,
            vale: v?.casosVale || 0,
            presico: v?.casosPresico || 0,
            monto: v?.monto,
            x: e.clientX - r.left,
            y: e.clientY - r.top,
        });
    }

    return (
        <div style={{ position: 'relative' }}>
            <svg ref={svgRef} viewBox={mexico.viewBox} role="img"
                 aria-label="Mapa de México: casos por estado y grupo de marcas"
                 style={{ width: '100%', height: 'auto', display: 'block' }}>
                {mexico.locations.map(loc => {
                    const v = valores[norm(loc.name)];
                    const conCasos = (v?.casos || 0) > 0;
                    const caja = cajas[loc.id];
                    const esSel = claveSel && claveSel === norm(loc.name);
                    return (
                        <g key={loc.id}>
                            {conCasos && caja && (
                                <>
                                    <clipPath id={`ho-iz-${loc.id}`}>
                                        <rect x={caja.x} y={caja.y} width={caja.w / 2} height={caja.h} />
                                    </clipPath>
                                    <clipPath id={`ho-de-${loc.id}`}>
                                        <rect x={caja.x + caja.w / 2} y={caja.y} width={caja.w / 2} height={caja.h} />
                                    </clipPath>
                                    <path d={loc.path} clipPath={`url(#ho-iz-${loc.id})`}
                                          fill={tono(v.casosVale, max, 'accent')} />
                                    <path d={loc.path} clipPath={`url(#ho-de-${loc.id})`}
                                          fill={tono(v.casosPresico, max, 'hecho')} />
                                </>
                            )}
                            {/* Trazo superior: borde, eventos y medición del bbox */}
                            <path data-id={loc.id} d={loc.path}
                                  fill={conCasos && caja ? 'transparent' : 'var(--c-msg-bg)'}
                                  stroke={esSel ? 'var(--ho-ink)' : 'var(--c-bg)'}
                                  strokeWidth={esSel ? 1.8 : 0.7}
                                  style={{ cursor: conCasos ? 'pointer' : 'default' }}
                                  onMouseMove={e => moverTooltip(e, loc, v)}
                                  onMouseLeave={() => setHover(null)}
                                  onClick={() => conCasos && onSelect?.(esSel ? '' : v.estado)} />
                        </g>
                    );
                })}
            </svg>

            {hover && (
                <div className="viz-tip" style={{
                    position: 'absolute', left: hover.x + 14, top: hover.y + 10,
                    pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 5,
                }}>
                    <div className="k">{hover.nombre}</div>
                    <div><strong>{hover.casos}</strong> {hover.casos === 1 ? 'caso' : 'casos'}</div>
                    {hover.casos > 0 && (
                        <div style={{ fontSize: 11.5 }}>
                            <span style={{ color: 'var(--ho-accent)' }}>■</span> Vale: {hover.vale}
                            {'  '}
                            <span style={{ color: 'var(--ho-hecho)' }}>■</span> Presico: {hover.presico}
                        </div>
                    )}
                    {hover.casos > 0 && hover.monto != null && (
                        <div style={{ color: 'var(--c-text-3)' }}>{fmtMonto(hover.monto)} de detrimento</div>
                    )}
                </div>
            )}

            <div className="d-flex flex-column gap-1" style={{ marginTop: 12, fontSize: 11, color: 'var(--c-text-3)' }}>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span style={{
                        flex: '0 0 110px', height: 8, borderRadius: 4,
                        background: 'linear-gradient(90deg, var(--c-msg-bg), color-mix(in srgb, var(--ho-accent) 90%, var(--c-card)))',
                    }} />
                    <span><strong style={{ color: 'var(--c-text-2)' }}>Mitad izquierda · azul:</strong> Vale Amigo, Viva Vale y Rapivale</span>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span style={{
                        flex: '0 0 110px', height: 8, borderRadius: 4,
                        background: 'linear-gradient(90deg, var(--c-msg-bg), color-mix(in srgb, var(--ho-hecho) 90%, var(--c-card)))',
                    }} />
                    <span><strong style={{ color: 'var(--c-text-2)' }}>Mitad derecha · rojo:</strong> Presico y sus marcas (La Casita, La Güerita…)</span>
                </div>
                <div>Escala compartida: 0 → {max} {max === 1 ? 'caso' : 'casos'} por grupo. Clic en un estado para enfocar el tablero; otro clic lo libera.</div>
            </div>
        </div>
    );
}
